// UI ↔ data адаптер для materials (Phase 2).
//
// Реальный backend через **PowerSync** (rules/offline-sync.md §2): materials /
// folders / glossary_terms — синкаемые таблицы, читаются из локальной
// SQLite через usePowerSyncQuery (offline-first, реактивно). НЕ tRPC.
//
// Адаптеры маппят локальную SQLite-строку (snake_case, title/kind) в legacy
// UI-DTO (camelCase, title/sourceType), используемый Library / Workspace / Sets.
//
// Detail (workspace): PowerSync primary + tRPC materials.get fallback при
// sync-miss после upload→navigate (см. useMaterialDetail).

import type {
	MaterialSetDto,
	MaterialsGetOutput,
	PublishableArtifactType,
	MaterialDto as SharedMaterialDto,
} from "@studyhub/shared/contracts/materials";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { remoteStatusFromQuery } from "@/hooks/remote-status-from-query";
import {
	resolveSyncedDetailPhase,
	type SyncedDetailPhase,
} from "@/hooks/resolve-synced-detail-phase";
import { useAwaitingSync } from "@/hooks/use-awaiting-sync";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { usePowerSyncStatus } from "@/hooks/use-powersync-status";
import { useCurrentUser } from "@/hooks/use-session";
import { softDeleteRow, updateRow } from "@/lib/powersync";
import type { LocalMaterial } from "@/lib/powersync/queries";
import { usePowerSyncQueryShim } from "@/lib/powersync/query-shim";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { api } from "@/lib/trpc-client";
import { trpcMutation, trpcQuery } from "@/lib/trpc-fetch";

// Re-export для legacy use-sites внутри components/materials/* — единый источник
// истины DTO набора живёт в shared-контракте (packages/shared/contracts/materials).
export type { MaterialSetDto };

// Legacy DTO, используемый UI компонентами Library / Workspace / Sets.
export interface MaterialDto {
	id: string;
	title: string;
	sourceType: string;
	language: string;
	status: string;
	errorCode?: string | null;
	errorMessage?: string | null;
	createdAt: string;
	/** Для сортировки «по изменению» в библиотеке материалов. */
	updatedAt: string;
	pageCount?: number;
	durationSec?: number;
	/** Первый набор материала (для set-badge на карточке; legacy single). */
	setId?: string;
	/** Все наборы материала (M2M через set_items, item_type='material'). */
	setIds?: string[];
	fileSizeBytes?: number;
	thumbHashBase64?: string | null;
	thumbnailUrl?: string | null;
	favorite?: boolean;
	tags?: string[];
	/** Извлечённый текст (text/note/doc/pdf parsed). */
	content?: string | null;
	/** Транскрипция (audio/video/youtube). */
	transcription?: string | null;
	/** Исходный URL (youtube/webpage) — для embed/внешней ссылки. */
	sourceUrl?: string | null;
	/** Кол-во готовых конспектов материала (PowerSync, реактивно). */
	notesCount?: number;
	/** Кол-во карточек, сгенерированных из материала (PowerSync, реактивно). */
	flashcardsCount?: number;
	/** Кол-во тестов, сгенерированных из материала (PowerSync, реактивно). */
	quizzesCount?: number;
}

export interface GlossaryTermDto {
	id: string;
	materialId: string;
	term: string;
	definition: string;
	examples?: string[];
	firstSeenChunkId?: string;
}

interface LocalMaterialSet {
	id: string;
	user_id: string;
	name: string;
	description: string | null;
	color: string | null;
	is_public: number | null;
	share_token: string | null;
	sort_order: number | null;
	favorite: number | null;
	materials_count: number | null;
	generated_flashcards: number | null;
	generated_tests: number | null;
	/** Миграция 0083: 'folder' (папка пользователя) | 'course_source' (состав курса). */
	kind: string | null;
	/** Миграция 0083: вложенность папок — колонка есть, UI появится позже. */
	parent_id: string | null;
	created_at: string | null;
	updated_at: string | null;
}

/** Дефолт HEX-цвета набора (indigo) — fallback когда PowerSync вернул NULL. */
const DEFAULT_SET_COLOR = "#6366F1";

interface LocalGlossaryTermRow {
	id: string;
	material_id: string;
	term: string;
	definition: string;
	/** jsonb string в SQLite PowerSync. */
	examples: string | null;
}

interface LocalSetItemRow {
	material_id: string;
	folder_id: string;
}

interface LocalArtifactCountRow {
	material_id: string;
	cnt: number;
}

/** tags ARRAY (Postgres) → JSON-строка в SQLite. Безопасный парс в string[]. */
function parseTags(raw: string | null): string[] {
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw) as unknown;
		return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === "string") : [];
	} catch {
		return [];
	}
}

function adaptMaterial(
	m: LocalMaterial,
	setIds: string[] = [],
	counts: { notes?: number; flashcards?: number; quizzes?: number } = {},
): MaterialDto {
	return {
		id: m.id,
		title: m.name,
		sourceType: m.type,
		language: m.language ?? m.detected_language ?? "en",
		status: m.processing_status,
		createdAt: m.created_at,
		updatedAt: m.updated_at ?? m.created_at,
		// Связь со set'ами — M2M через set_items (item_type='material'; плоской
		// колонки set_id в materials нет). setId — первый набор (для одиночного badge).
		setIds,
		setId: setIds[0],
		fileSizeBytes: m.file_size_bytes ?? undefined,
		thumbHashBase64: m.thumb_hash_base64 ?? null,
		thumbnailUrl: m.thumbnail_url ?? m.youtube_thumbnail ?? null,
		// SQLite/PowerSync: bool как 0/1; терпим true/"1"/"true" после драйверов.
		favorite: (() => {
			const v = m.favorite as unknown;
			return v === 1 || v === true || v === "1" || v === "true";
		})(),
		tags: parseTags(m.tags),
		// pageCount недоступен реальной схемой materials — UI fallback на undefined.
		pageCount: undefined,
		durationSec: m.duration ?? undefined,
		content: m.content ?? null,
		transcription: m.transcription ?? null,
		// Для youtube/webpage в original_file хранится исходный URL (см.
		// серверный buildSignedUrl). Для файловых — это S3 key (не URL),
		// поэтому отдаём только для url-типов.
		sourceUrl:
			m.type === "youtube" || m.type === "webpage" || m.type === "website"
				? (m.original_file ?? null)
				: null,
		notesCount: counts.notes ?? 0,
		flashcardsCount: counts.flashcards ?? 0,
		quizzesCount: counts.quizzes ?? 0,
	};
}

/** Shared contract MaterialDto / MaterialsGetOutput → UI MaterialDto. */
export function adaptSharedMaterial(
	m: SharedMaterialDto | MaterialsGetOutput,
	counts: { notes?: number; flashcards?: number; quizzes?: number } = {},
): MaterialDto {
	const withExtras = m as MaterialsGetOutput;
	const setIds = m.sets.map((s) => s.id);
	return {
		id: m.id,
		title: m.name,
		sourceType: m.type,
		language: m.language ?? "en",
		status: m.processingStatus,
		errorCode: "errorCode" in m ? ((m as { errorCode?: string | null }).errorCode ?? null) : null,
		errorMessage:
			"errorMessage" in m ? ((m as { errorMessage?: string | null }).errorMessage ?? null) : null,
		createdAt: m.createdAt,
		updatedAt: m.updatedAt ?? m.createdAt,
		setIds,
		setId: setIds[0],
		fileSizeBytes: m.fileSizeBytes,
		thumbHashBase64: m.thumbHashBase64,
		thumbnailUrl: m.thumbnailUrl ?? m.youtubeThumbnail,
		favorite: m.favorite,
		tags: m.tags,
		pageCount: undefined,
		durationSec: m.duration ?? undefined,
		content: withExtras.content ?? null,
		transcription: withExtras.transcription ?? null,
		// Раньше здесь стоял жёсткий null — и на remote-пути (PowerSync ещё не
		// догнал строку) вкладка «Источник» теряла адрес видео: вместо плеера
		// рисовалась пустая карточка. Сервер отдаёт sourceUrl для youtube/webpage.
		sourceUrl: m.sourceUrl,
		notesCount: counts.notes ?? 0,
		flashcardsCount: counts.flashcards ?? 0,
		quizzesCount: counts.quizzes ?? 0,
	};
}

/** Pad create/finalize MaterialDto → MaterialsGetOutput for tRPC cache seed. */
export function toGetOutputSeed(m: SharedMaterialDto): MaterialsGetOutput {
	return {
		...m,
		content: null,
		transcription: null,
		notes: null,
		chunkPreview: [],
	};
}

type MaterialsGetUtils = ReturnType<typeof api.useUtils>["materials"]["get"];

/** Seed materials.get cache before navigate so workspace opens without waiting for PowerSync. */
export function seedMaterialGetCache(
	getUtils: MaterialsGetUtils,
	material: SharedMaterialDto,
): void {
	getUtils.setData({ id: material.id }, toGetOutputSeed(material));
}

export interface MaterialDetailResult {
	data: MaterialDto | undefined;
	phase: SyncedDetailPhase;
	source: "local" | "remote" | undefined;
	refetch: () => void;
}

/**
 * Non-terminal processing steps на materials.processing_status.
 * Пока локальный статус в этом наборе — держим tRPC materials.get включённым:
 * Socket `document:ready` инвалидирует Query, но после первого PS-hit query
 * раньше был disabled → detail залипал в processing до download-sync.
 */
export const MATERIAL_IN_FLIGHT_STATUSES = new Set([
	"queued",
	"uploading",
	"parsing",
	"ocr",
	"chunking",
	"embedding",
	"processing",
]);

export function isMaterialStatusInFlight(status: string | undefined): boolean {
	return Boolean(status && MATERIAL_IN_FLIGHT_STATUSES.has(status));
}

/**
 * Merge detail: PowerSync primary, но terminal remote status перекрывает
 * устаревший in-flight local (лаг download-sync после document:ready).
 */
export function mergeMaterialDetail(
	local: MaterialDto | undefined,
	remote: MaterialDto | undefined,
): MaterialDto | undefined {
	if (!local) return remote;
	if (!remote) return local;
	if (isMaterialStatusInFlight(local.status) && !isMaterialStatusInFlight(remote.status)) {
		return {
			...local,
			status: remote.status,
			errorCode: remote.errorCode ?? local.errorCode,
			errorMessage: remote.errorMessage ?? local.errorMessage,
			content: local.content ?? remote.content,
			transcription: local.transcription ?? remote.transcription,
			sourceUrl: local.sourceUrl ?? remote.sourceUrl,
		};
	}
	return local;
}

/**
 * Detail material: PowerSync primary, materials.get fallback while SQLite lags
 * (upload→navigate / fresh account reconnect) **и** пока local status ещё
 * in-flight (terminal remote перекрывает stale PS).
 */
export function useMaterialDetail(id: string): MaterialDetailResult {
	const localQuery = useMaterial(id);
	const isOnline = useOnlineStatus();
	const { hasSynced } = usePowerSyncStatus();
	const hasLocal = Boolean(localQuery.data);
	const localInFlight = isMaterialStatusInFlight(localQuery.data?.status);
	// (a) нет локальной строки — cold start / upload→navigate;
	// (b) local ещё processing — PS может отставать от server ready.
	const remoteEnabled = Boolean(id) && isOnline && (!hasLocal || localInFlight);

	const remoteQuery = api.materials.get.useQuery(
		{ id },
		{
			enabled: remoteEnabled,
			retry: false,
			// Seed pads content:null — must revalidate so Source/text fills in.
			staleTime: 0,
			// Пока материал обрабатывается — poll как fallback к Socket invalidation.
			refetchInterval: localInFlight ? 3_000 : false,
		},
	);

	const remoteUi = useMemo(() => {
		if (!remoteQuery.data) return undefined;
		return adaptSharedMaterial(remoteQuery.data);
	}, [remoteQuery.data]);

	const data = useMemo(
		() => mergeMaterialDetail(localQuery.data, remoteUi),
		[localQuery.data, remoteUi],
	);
	const source: "local" | "remote" | undefined = localQuery.data
		? "local"
		: remoteUi
			? "remote"
			: undefined;

	const awaitingSync = useAwaitingSync(Boolean(data));
	const remoteStatus = remoteStatusFromQuery({
		isFetching: remoteQuery.isFetching,
		isError: remoteQuery.isError,
		error: remoteQuery.error,
		hasData: Boolean(remoteQuery.data),
		enabled: remoteEnabled,
	});

	const phase = resolveSyncedDetailPhase({
		hasData: Boolean(data),
		awaitingSync,
		hasSynced,
		isOnline,
		remoteStatus,
		localError: localQuery.isError,
	});

	// While PowerSync still loading first paint and we have no remote yet — keep loading.
	const effectivePhase: SyncedDetailPhase =
		phase === "not_found" && localQuery.isLoading ? "loading" : phase;

	return {
		data,
		phase: effectivePhase,
		source,
		refetch: () => {
			void localQuery.refetch();
			void remoteQuery.refetch();
		},
	};
}

/**
 * Кол-во конспектов / карточек / тестов по материалу из синкаемых данных
 * PowerSync. Конспекты — через `notes.material_id` (только `generation_status =
 * 'ready'`, на материал бывает несколько — по уровню детализации). Карточки —
 * через `flashcard_sets.generated_from_material_id` (набор сгенерирован из
 * материала), тесты — через `tests.source_material_id`. Группировка на клиенте
 * (GROUP BY в локальной SQLite допустим, в отличие от stream-queries sync-rules).
 * Один запрос на тип → Map, чтобы не плодить N запросов на список карточек.
 */
function useArtifactCounts(userId: string | null) {
	const notesQuery = usePowerSyncQuery<LocalArtifactCountRow>(
		`SELECT material_id AS material_id, COUNT(*) AS cnt
		   FROM notes
		  WHERE user_id = ? AND deleted_at IS NULL AND material_id IS NOT NULL
		    AND generation_status = 'ready'
		GROUP BY material_id`,
		[userId ?? ""],
	);
	const flashcardsQuery = usePowerSyncQuery<LocalArtifactCountRow>(
		`SELECT s.generated_from_material_id AS material_id, COUNT(f.id) AS cnt
		   FROM flashcards f
		   JOIN flashcard_sets s ON s.id = f.folder_id
		  WHERE s.user_id = ? AND s.deleted_at IS NULL AND f.deleted_at IS NULL
		    AND s.generated_from_material_id IS NOT NULL
		GROUP BY s.generated_from_material_id`,
		[userId ?? ""],
	);
	const quizzesQuery = usePowerSyncQuery<LocalArtifactCountRow>(
		`SELECT source_material_id AS material_id, COUNT(*) AS cnt
		   FROM tests
		  WHERE user_id = ? AND deleted_at IS NULL AND source_material_id IS NOT NULL
		GROUP BY source_material_id`,
		[userId ?? ""],
	);
	return useMemo(() => {
		const notes = new Map<string, number>(
			(notesQuery.data ?? []).map((r) => [r.material_id, r.cnt]),
		);
		const flashcards = new Map<string, number>(
			(flashcardsQuery.data ?? []).map((r) => [r.material_id, r.cnt]),
		);
		const quizzes = new Map<string, number>(
			(quizzesQuery.data ?? []).map((r) => [r.material_id, r.cnt]),
		);
		return { notes, flashcards, quizzes };
	}, [notesQuery.data, flashcardsQuery.data, quizzesQuery.data]);
}

/**
 * Membership-map `materialId → setId[]` из полиморфного pivot set_items
 * (item_type='material'). Отдельный реактивный PowerSync-запрос (таблица
 * синкается, см. lib/powersync/schema.ts). Группировка на клиенте через map.
 */
function useSetMembership(userId: string | null) {
	const query = usePowerSyncQuery<LocalSetItemRow>(
		`SELECT i.item_id AS material_id, i.folder_id
		   FROM set_items i
		   JOIN materials m ON m.id = i.item_id
		  WHERE i.item_type = 'material' AND m.user_id = ? AND m.deleted_at IS NULL`,
		[userId ?? ""],
	);
	return useMemo(() => {
		const map = new Map<string, string[]>();
		for (const r of query.data ?? []) {
			const existing = map.get(r.material_id);
			if (existing) existing.push(r.folder_id);
			else map.set(r.material_id, [r.folder_id]);
		}
		return map;
	}, [query.data]);
}

export function useMaterialsList() {
	const user = useCurrentUser();
	// РЕШЕНИЕ(create-from-chat): derived-материал чата — первоклассный видимый
	// материал (откат скрытия). Раньше здесь стоял `AND source_kind IS NULL`,
	// прятавший derived-материалы диалога («Создать на основе чата») из
	// browse-списка, пикеров и nav-badges — это было ошибкой. Теперь они
	// видны как обычные материалы (конспект из чата резолвит имя источника).
	const query = usePowerSyncQuery<LocalMaterial>(
		`SELECT * FROM materials
		   WHERE user_id = ? AND deleted_at IS NULL
		ORDER BY updated_at DESC`,
		[user?.id ?? ""],
	);
	const membership = useSetMembership(user?.id ?? null);
	const counts = useArtifactCounts(user?.id ?? null);
	const data = useMemo(() => {
		const rows = query.data ?? [];
		return {
			items: rows.map((m) =>
				adaptMaterial(m, membership.get(m.id) ?? [], {
					notes: counts.notes.get(m.id),
					flashcards: counts.flashcards.get(m.id),
					quizzes: counts.quizzes.get(m.id),
				}),
			),
			total: rows.length,
		};
	}, [query.data, membership, counts]);
	return usePowerSyncQueryShim(query, data);
}

export function useMaterial(id: string) {
	const user = useCurrentUser();
	const query = usePowerSyncQuery<LocalMaterial>(
		`SELECT * FROM materials WHERE id = ? AND deleted_at IS NULL`,
		[id || ""],
	);
	const membership = useSetMembership(user?.id ?? null);
	const counts = useArtifactCounts(user?.id ?? null);
	const data = useMemo(() => {
		const row = query.data?.[0];
		return row
			? adaptMaterial(row, membership.get(row.id) ?? [], {
					notes: counts.notes.get(row.id),
					flashcards: counts.flashcards.get(row.id),
					quizzes: counts.quizzes.get(row.id),
				})
			: undefined;
	}, [query.data, membership, counts]);
	return usePowerSyncQueryShim(query, data);
}

export function useMaterialSets() {
	const user = useCurrentUser();
	// Служебные наборы состава курса (kind='course_source') в Библиотеке скрыты —
	// это не папки пользователя, а внутренний контейнер курса (VISION §2).
	const setsQuery = usePowerSyncQuery<LocalMaterialSet>(
		`SELECT * FROM folders
		   WHERE user_id = ? AND deleted_at IS NULL
		     AND ifnull(kind, 'folder') <> 'course_source'
		ORDER BY updated_at DESC`,
		[user?.id ?? ""],
	);
	// Кол-во материалов в сете — через полиморфный set_items (item_type='material',
	// связь many-to-many), не через несуществующую плоскую колонку materials.set_id.
	const countsQuery = usePowerSyncQuery<{ folder_id: string; cnt: number }>(
		`SELECT i.folder_id, COUNT(*) AS cnt
		   FROM set_items i
		   JOIN materials m ON m.id = i.item_id
		  WHERE i.item_type = 'material' AND m.user_id = ? AND m.deleted_at IS NULL
		GROUP BY i.folder_id`,
		[user?.id ?? ""],
	);
	const data = useMemo(() => {
		const counts = new Map((countsQuery.data ?? []).map((r) => [r.folder_id, r.cnt]));
		const nowIso = new Date().toISOString();
		const items: MaterialSetDto[] = (setsQuery.data ?? []).map((s) => ({
			id: s.id,
			name: s.name,
			description: s.description ?? null,
			color: s.color ?? DEFAULT_SET_COLOR,
			isPublic: Boolean(s.is_public),
			shareToken: s.share_token ?? null,
			// publicUrl собирается на сервере из APP_BASE_URL — PowerSync публикует
			// только share_token. Клиенту достаточно токена для отображения статуса;
			// фактический URL запрашивается через folders.share / folders.get при действии.
			publicUrl: null,
			sortOrder: s.sort_order ?? 0,
			favorite: Boolean(s.favorite),
			// Миграция 0083: папка vs служебный набор курса + вложенность (UI позже).
			kind: s.kind === "course_source" ? "course_source" : "folder",
			parentId: s.parent_id ?? null,
			// PowerSync-кэш materials_count может отставать от M2M-pivot — приоритет
			// у live-count из set_items.
			materialsCount: counts.get(s.id) ?? s.materials_count ?? 0,
			generatedFlashcards: s.generated_flashcards ?? 0,
			generatedTests: s.generated_tests ?? 0,
			// auto_pack_status — server-only, не в PowerSync; UI берёт из folders.get.
			autoPackStatus: null,
			createdAt: s.created_at ?? nowIso,
			updatedAt: s.updated_at ?? nowIso,
		}));
		return { items };
	}, [setsQuery.data, countsQuery.data]);
	return usePowerSyncQueryShim(setsQuery, data);
}

function parseGlossaryExamples(raw: string | null | undefined): string[] {
	if (!raw) return [];
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter((x): x is string => typeof x === "string");
	} catch {
		return [];
	}
}

export function useGlossary(materialId: string | undefined) {
	const query = usePowerSyncQuery<LocalGlossaryTermRow>(
		`SELECT id, material_id, term, definition, examples FROM glossary_terms
		   WHERE material_id = ?
		ORDER BY LOWER(term) ASC`,
		[materialId ?? ""],
	);
	const data = useMemo(() => {
		const items: GlossaryTermDto[] = (query.data ?? []).map((g) => ({
			id: g.id,
			materialId: g.material_id,
			term: g.term,
			definition: g.definition,
			examples: parseGlossaryExamples(g.examples),
		}));
		return { items };
	}, [query.data]);
	return { ...usePowerSyncQueryShim(query, data), enabled: !!materialId };
}

// ===== Артефакты материала (PowerSync, реактивно) =====
//
// Счётчики/статусы карточек и тестов для табов workspace и правой панели
// «Из этого материала». Источник истины — локальная SQLite (read-from-source,
// CLAUDE.md §13/§14): строки flashcard_sets/tests синкаются PowerSync'ом, UI
// обновляется реактивно без Socket.IO-инвалидаций и F5 (глоссарий/конспект
// уже работали так; tRPC-версии залипали до reload при потере события).

export interface LocalMaterialFlashcardSetRow {
	id: string;
	name: string | null;
	processing_status: string | null;
	card_count: number;
}

/** Наборы карточек материала с live-количеством карточек (PowerSync). */
export function useLocalFlashcardSetsForMaterial(materialId: string | undefined) {
	return usePowerSyncQuery<LocalMaterialFlashcardSetRow>(
		`SELECT s.id, s.name, s.processing_status,
		        (SELECT COUNT(*) FROM flashcards f
		          WHERE f.folder_id = s.id AND f.deleted_at IS NULL) AS card_count
		   FROM flashcard_sets s
		  WHERE s.generated_from_material_id = ? AND s.deleted_at IS NULL
		ORDER BY s.created_at ASC`,
		[materialId ?? ""],
	);
}

export interface LocalMaterialQuizRow {
	id: string;
	name: string | null;
	questions_count: number | null;
	generation_status: string | null;
}

/** Тесты материала со статусом генерации (PowerSync). */
export function useLocalQuizzesForMaterial(materialId: string | undefined) {
	return usePowerSyncQuery<LocalMaterialQuizRow>(
		`SELECT id, name, questions_count, generation_status
		   FROM tests
		  WHERE source_material_id = ? AND deleted_at IS NULL
		ORDER BY created_at ASC`,
		[materialId ?? ""],
	);
}

// ===== Trash (soft-deleted материалы) =====
//
// Корзина — server-only поток: soft-deleted материалы НЕ синкаются PowerSync
// (sync rule фильтрует `deleted_at IS NULL`), поэтому читаем через tRPC
// `materials.trash.list`. Восстановление/очистка — tRPC mutations.

export interface TrashItemDto {
	id: string;
	title: string;
	sourceType: string;
	fileSizeBytes: number;
	thumbHashBase64: string | null;
	thumbnailUrl: string | null;
	/** ISO datetime soft-delete; permanent delete через 30 дней после. */
	deletedAt: string;
	/** Дней до безвозвратного удаления (computed на клиенте от deletedAt). */
	daysLeft: number;
}

interface TrashListResponse {
	items: Array<{
		id: string;
		name: string;
		type: string;
		fileSizeBytes: number;
		thumbHashBase64: string | null;
		thumbnailUrl: string | null;
		deletedAt: string;
	}>;
	nextCursor: string | null;
}

const TRASH_RETENTION_DAYS = 30;

function daysUntilPurge(deletedAtIso: string): number {
	const deletedMs = new Date(deletedAtIso).getTime();
	if (Number.isNaN(deletedMs)) return TRASH_RETENTION_DAYS;
	const purgeMs = deletedMs + TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
	const diffDays = Math.ceil((purgeMs - Date.now()) / (24 * 60 * 60 * 1000));
	return Math.max(0, diffDays);
}

export function useTrashList() {
	return useQuery({
		queryKey: ["materials", "trash", "list"],
		queryFn: async (): Promise<{ items: TrashItemDto[] }> => {
			const res = await trpcQuery<TrashListResponse>("materials.trash.list", {
				limit: 100,
			});
			return {
				items: res.items.map((r) => ({
					id: r.id,
					title: r.name,
					sourceType: r.type,
					fileSizeBytes: r.fileSizeBytes,
					thumbHashBase64: r.thumbHashBase64,
					thumbnailUrl: r.thumbnailUrl,
					deletedAt: r.deletedAt,
					daysLeft: daysUntilPurge(r.deletedAt),
				})),
			};
		},
		staleTime: 15_000,
	});
}

export function useRestoreMaterial() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => trpcMutation<unknown>("materials.restore", { id }),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["materials"] });
			void qc.invalidateQueries({ queryKey: ["material"] });
			void qc.invalidateQueries({ queryKey: ["materials", "trash", "list"] });
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
			void qc.invalidateQueries({ queryKey: ["glossary"] });
			void qc.invalidateQueries({ queryKey: ["weak-spots"] });
			void qc.invalidateQueries({ queryKey: ["weakSpots"] });
		},
	});
}

export function useEmptyTrash() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () => trpcMutation<{ scheduledCount: number }>("materials.trash.empty", {}),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["materials"] });
			void qc.invalidateQueries({ queryKey: ["material"] });
			void qc.invalidateQueries({ queryKey: ["materials", "trash", "list"] });
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
			void qc.invalidateQueries({ queryKey: ["glossary"] });
			void qc.invalidateQueries({ queryKey: ["weak-spots"] });
			void qc.invalidateQueries({ queryKey: ["weakSpots"] });
		},
	});
}

// ===== Sets — share =====
//
// sets.share генерирует стабильный nanoid(32) share token + реальный
// publicUrl (`{base}/materials/shared/{token}`). Повторный вызов идемпотентен.

export function useShareSet() {
	return useMutation({
		mutationFn: (setId: string) =>
			trpcMutation<{ shareToken: string; publicUrl: string }>("folders.share", { id: setId }),
	});
}

// ===== Sets — CRUD / actions =====
//
// Все мутации реальные (sets.* tRPC процедуры P0.1). PowerSync синкает
// folders автоматически — invalidation не требуется, но useMaterialSets
// (PowerSync-query) реактивно обновится. На случай legacy-консюмеров, которые
// читают через react-query (`folders.list`), инвалидируем tRPC-префикс [["folders","list"]].

interface UpdateSetInput {
	id: string;
	name?: string;
	description?: string | null;
	color?: string;
	favorite?: boolean;
}

export function useUpdateSet() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: UpdateSetInput) => trpcMutation<MaterialSetDto>("folders.update", input),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [["folders", "list"]] });
		},
	});
}

/**
 * Dual-write избранного коллекции: локальный PowerSync + sets.update.
 * Зеркало useToggleMaterialFavorite — UI не ждёт download-sync.
 */
export function useToggleSetFavorite(setId: string) {
	return useMutation({
		mutationFn: async ({ currentlyFavorited }: { currentlyFavorited: boolean }) => {
			const next = !currentlyFavorited;
			await updateRow("folders", setId, { favorite: next ? 1 : 0 });
			try {
				await trpcMutation<unknown>("folders.update", {
					id: setId,
					favorite: next,
				});
			} catch {
				// Offline / сеть: локальная запись остаётся, PowerSync upload догонит.
			}
			return { isFavorited: next };
		},
	});
}

export function useDeleteSet() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (setId: string) => {
			// Soft-delete коллекции offline; cascade артефактов — на сервере при upload.
			await softDeleteRow("folders", setId);
			return { ok: true as const };
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [["folders", "list"]] });
			void qc.invalidateQueries({ queryKey: ["trash"] });
			void qc.invalidateQueries({ queryKey: ["notes", "list"] });
			void qc.invalidateQueries({ queryKey: ["flashcard-sets"] });
		},
	});
}

export function useRevokeShareSet() {
	return useMutation({
		mutationFn: (setId: string) => trpcMutation<{ ok: true }>("folders.revokeShare", { id: setId }),
	});
}

// sets.copy — асинхронная операция через Inngest workflow (P2.1).
// Mutation возвращает `{ jobId, newSetId }` СРАЗУ после INSERT placeholder-набора;
// реальная копия материалов идёт в фоне (S3 server-side copy для каждого material).
// Прогресс — Socket.IO события `folders:copy:progress` / `folders:copy:ready` / `folders:copy:failed`,
// обработчики глобально в /materials/sets route.
export function useCopySet() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (setId: string) =>
			trpcMutation<{ jobId: string; newSetId: string }>("folders.copy", { id: setId }),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [["folders", "list"]] });
		},
	});
}

// sets.export — триггерит Inngest exportSet → ZIP. Presigned URL придёт
// через Socket.IO; UI здесь только показывает toast «Готовим архив».
export function useExportSet() {
	return useMutation({
		mutationFn: (setId: string) => trpcMutation<{ jobId: string }>("folders.export", { id: setId }),
	});
}

// folders.setArtifactPublic — публикация/снятие публикации артефакта (Collections
// §12, D6.1). is_public — server-managed; меняется только этой мутацией
// (PowerSync write для is_public запрещён). После успеха инвалидируем folders.get,
// чтобы группировка перечитала актуальное состояние публичности.
export function useSetArtifactPublic() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (vars: { itemType: PublishableArtifactType; itemId: string; isPublic: boolean }) =>
			trpcMutation<{ itemType: PublishableArtifactType; itemId: string; isPublic: boolean }>(
				"folders.setArtifactPublic",
				vars,
			),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [["folders", "get"]] });
		},
	});
}

// ===== Sets — membership материалов =====
//
// addMaterials / removeMaterial управляют pivot set_items (item_type='material').
// PowerSync синкает таблицу обратно (см. lib/powersync/schema.ts) → useMaterialsList /
// useMaterialSets реактивно обновятся (membership-map + materialsCount). На
// случай legacy-консюмеров react-query инвалидируем [["folders","list"]].

// sets.addMaterials — bulk-добавление существующих материалов в набор
// (bulkAddMaterials, idempotent ON CONFLICT). Input — { setId, materialIds[] }.
export function useAddMaterialsToSet() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (vars: { setId: string; materialIds: string[] }) =>
			trpcMutation<{ ok: true }>("folders.addMaterials", vars),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [["folders", "list"]] });
		},
	});
}

// sets.removeMaterial — убрать один материал из набора (сам материал не
// удаляется, только связь в pivot). Input — { setId, materialId }.
export function useRemoveMaterialFromSet() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (vars: { setId: string; materialId: string }) =>
			trpcMutation<{ ok: true }>("folders.removeMaterial", vars),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [["folders", "list"]] });
		},
	});
}
